import type {
  KYCQuestionDefinition,
  KYCQuestionnaireElement,
  KYCQuestionnaireSubmission,
} from '@anchorage/graphql/generated';
import { useDeepCompare } from '@anchorage/hooks';
import type { reactHookForm } from '@latitude/form';
import { useCallback, useMemo } from 'react';

import { getSectionViews } from './sections';
import type { FormAnswer, ShouldShowElement } from './types';
import {
  addQuestionAtoms,
  convertQuestionnaireAnswers,
  getAllNavigableNavElements,
  getClosestSectionTitle,
  getElementQuestions,
  getHiddenGroupQuestionPaths,
  getHiddenQuestionIDs,
  getNavElements,
  getQuestion,
  getQuestionNavElementID,
  questionIDsByAnsweredOptions,
  showElementBasedOnAnsweredOptions,
} from './utils';

export const useQuestionnaire = ({
  submission,
  answers,
  formStateErrors = {},
}: {
  submission?: KYCQuestionnaireSubmission;
  answers?: Record<string, FormAnswer>;
  formStateErrors?: reactHookForm.FieldErrors<Record<string, FormAnswer>>;
} = {}) => {
  const allQuestions = useMemo(
    () =>
      submission?.body?.elements.reduce(
        (acc, page) => [...acc, ...getElementQuestions(page)],
        [] as KYCQuestionDefinition[],
      ),
    [submission?.body?.elements],
  );

  const initialAnswers = useMemo(
    () =>
      submission?.answers
        ? convertQuestionnaireAnswers(submission.answers, allQuestions)
        : {},
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    useDeepCompare([submission?.answers, allQuestions]),
  );

  const shouldShowElement = useCallback(
    ({
      triggerOptionID,
      answeredOptions,
      questions = allQuestions,
      groupNameAndIndex,
    }: ShouldShowElement) =>
      showElementBasedOnAnsweredOptions({
        triggerOptionID,
        answeredOptions,
        questions,
        groupNameAndIndex,
      }),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const answeredOptions = useMemo(() => {
    if (!answers) {
      return undefined;
    }
    const optionsOnly = questionIDsByAnsweredOptions(answers);
    return submission?.body
      ? addQuestionAtoms({
          body: submission.body,
          answers,
          answeredOptions: optionsOnly,
          allQuestions,
        })
      : optionsOnly;
  }, [answers, submission?.body, allQuestions]);

  const hiddenQuestionIDs = useMemo(
    () =>
      submission?.body
        ? getHiddenQuestionIDs({
            body: submission.body,
            answeredOptions: answeredOptions ?? {},
            allQuestions,
          })
        : [],
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    useDeepCompare([submission?.body, answeredOptions, allQuestions]),
  );

  // Only the number of rows matters below, and it changes less often than
  // the answers, so the hidden paths stay stable while the user types.
  const groupRowCounts = useMemo(() => {
    const rowCounts: Record<string, number> = {};
    for (const [questionID, answer] of Object.entries(answers ?? {})) {
      if (answer?.group_answers) {
        rowCounts[questionID] = answer.group_answers.length;
      }
    }
    return rowCounts;
  }, [answers]);

  // Every form path the form does not show right now: the hidden question ids
  // plus the triggered questions hidden inside the rows of the shown groups.
  const hiddenQuestionPaths = useMemo(
    () => [
      ...hiddenQuestionIDs,
      ...(answers && allQuestions
        ? getHiddenGroupQuestionPaths({
            answers,
            answeredOptions: answeredOptions ?? {},
            allQuestions,
            hiddenQuestionIDs,
          })
        : []),
    ],
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    useDeepCompare([
      hiddenQuestionIDs,
      answeredOptions,
      allQuestions,
      groupRowCounts,
    ]),
  );

  const getTriggeredElements = useCallback(
    (elements: KYCQuestionnaireElement[]) => {
      return elements.filter((element) => {
        const shouldShow = shouldShowElement?.({
          triggerOptionID: element?.triggeredBy,
          answeredOptions,
        });
        return shouldShow;
      });
    },
    [answeredOptions, shouldShowElement],
  );

  const triggeredNavElements = useMemo(() => {
    const triggeredMainNavs =
      submission?.body?.elements &&
      getTriggeredElements(submission?.body?.elements);

    // for each main nav element, get the triggered sub nav elements
    return triggeredMainNavs?.reduce<KYCQuestionnaireElement[]>(
      (acc, element) => {
        const subNavElements = getNavElements(element.elements);
        return [
          ...acc,
          {
            ...element,
            elements: subNavElements.length
              ? getTriggeredElements(subNavElements)
              : element.elements,
          },
        ];
      },
      [],
    );
  }, [submission?.body?.elements, getTriggeredElements]);

  const pages = useMemo(
    () =>
      triggeredNavElements && getAllNavigableNavElements(triggeredNavElements),
    [triggeredNavElements],
  );

  // The mobile views, one per visible section (see sections.ts).
  const sectionViews = useMemo(
    () =>
      pages
        ? getSectionViews(pages, (element) =>
            shouldShowElement({
              triggerOptionID: element.triggeredBy,
              answeredOptions,
            }),
          )
        : [],
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    useDeepCompare([pages, answeredOptions]),
  );

  // Every view in document order, hidden pages and sections included. Built
  // once per questionnaire body, not per answer change, so a consumer can
  // find the neighbours of a section that a trigger hides.
  const allSectionViews = useMemo(
    () =>
      submission?.body?.elements
        ? getSectionViews(
            getAllNavigableNavElements(submission.body.elements),
            () => true,
          )
        : [],
    [submission?.body?.elements],
  );

  const orderedQuestionIds = useMemo(
    () =>
      pages?.reduce<string[]>((acc, page) => {
        const pageQuestions = getElementQuestions(page);
        return [...acc, ...pageQuestions.map((question) => question.id)];
      }, []),
    [pages],
  );

  const orderedErrors = useMemo(
    () => {
      const errors = Object.keys(formStateErrors);
      const ordered = errors
        .map((errorKey) => {
          const error = formStateErrors[errorKey];
          const isGroupError = !!error?.group_answers?.length;
          const question = getQuestion(pages || [], errorKey);
          const questionDescription = isGroupError
            ? 'Add or correct the information'
            : error?.freeformText?.message ||
              error?.options?.message ||
              error?.file?.message;

          return {
            title:
              question?.text || getClosestSectionTitle(pages || [], errorKey),
            description: questionDescription,
            navElementID: getQuestionNavElementID(pages || [], errorKey),
            questionID: errorKey,
          };
        })
        .sort((left, right) => {
          if (left.navElementID && right.navElementID && orderedQuestionIds) {
            return (
              orderedQuestionIds.indexOf(left.questionID) -
              orderedQuestionIds.indexOf(right.questionID)
            );
          }
          return 0;
        });
      return ordered;
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    useDeepCompare([pages, Object.keys(formStateErrors), orderedQuestionIds]),
  );

  const scrollToQuestion = (questionId: string) => {
    setTimeout(() => {
      const element = document.querySelector(
        `[data-question-id="${questionId}"]`,
      );
      element?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const allPossiblePages = useMemo(
    () =>
      submission?.body?.elements
        .map((page) => page.elements.map((subPage) => subPage))
        .flat() || [],
    [submission?.body?.elements],
  );

  return {
    allPossiblePages,
    shouldShowElement,
    initialAnswers,
    answeredOptions,
    hiddenQuestionIDs,
    hiddenQuestionPaths,
    orderedErrors,
    pages,
    sectionViews,
    allSectionViews,
    scrollToQuestion,
    triggeredNavElements,
    allQuestions,
  };
};
